// Petits utilitaires d'interface partagés par toutes les vues.

import { icone } from "./icones.js";

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/** Échappe le HTML : tout texte venant d'un membre passe obligatoirement par ici. */
export const esc = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** Échappe puis transforme les URL en liens et garde les retours à la ligne. */
export function texteRiche(s = "") {
  return esc(s)
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\n/g, "<br>");
}

/* `initiales` et `couleurAvatar` vivaient ici : plus personne ne les appelait
   depuis que l'avatar vide affiche la silhouette (comme Facebook) plutôt que
   les initiales colorées. Retirées le 20 octobre 2026 (audit) — archive :
   ~/Desktop/koinonia-archive-code-retire-2026-10-20.zip. */

/**
 * Avatar d'un membre. Sans photo, on affiche la silhouette par défaut (comme Facebook)
 * plutôt que les initiales.
 */
/**
 * @param {object} user
 * @param {number} taille
 * @param {object} [o]  { anneau } — cercle bleu autour de la photo quand le
 *                      membre a un statut en cours, comme sur Facebook.
 */
export function avatar(user, taille = 40, o = {}) {
  const nom = user?.nom || "Membre";
  const style = `width:${taille}px;height:${taille}px`;
  const anneau = o.anneau ? " avatar--statut" : "";
  if (user?.photo)
    return `<img class="avatar${anneau}" style="${style}" src="${esc(user.photo)}" alt="${esc(nom)}" loading="lazy">`;
  return `<span class="avatar avatar--vide${anneau}" style="${style}" role="img" aria-label="${esc(nom)}">${icone("avatarVide", {
    taille,
    plein: true,
  })}</span>`;
}

/**
 * Ouvre une image en plein écran.
 *
 * Une photo de profil est une photo comme une autre : on doit pouvoir la
 * regarder. Sans cela, l'avatar de 132 px est tout ce qu'on verra jamais du
 * visage de quelqu'un.
 */
export function apercuImage(url, legende = "", actions = []) {
  if (!url) return;
  const fond = document.createElement("div");
  fond.className = "apercu-image";
  /* ⚠️ LA BARRE DES GESTES — 24 octobre 2026, demandé par Josaphat : regarder
     une photo en entier ne doit pas faire sortir de la conversation. Chaque
     action dit si l'aperçu reste ouvert (`garder`) — l'Amen reste, aller aux
     commentaires ferme. */
  fond.innerHTML = `
    <button class="apercu-image__fermer" aria-label="Fermer">${icone("fermer", { taille: 22 })}</button>
    <img src="${esc(url)}" alt="${esc(legende)}">
    ${legende ? `<p class="apercu-image__legende">${esc(legende)}</p>` : ""}
    ${actions.length ? `<div class="apercu-image__actions">${actions
      .map((a, i) => `<button type="button" class="apercu-image__action" data-action="${i}">
        ${a.icone ? icone(a.icone, { taille: 19 }) : ""}<span>${esc(a.texte)}</span>
      </button>`).join("")}</div>` : ""}`;
  actions.forEach((a, i) => {
    fond.querySelector(`[data-action="${i}"]`)?.addEventListener("click", (e) => {
      e.stopPropagation();
      a.surClic?.();
      if (!a.garder) fermer();
    });
  });
  document.body.appendChild(fond);
  document.body.classList.add("no-scroll");

  const fermer = () => {
    fond.remove();
    document.body.classList.remove("no-scroll");
    document.removeEventListener("keydown", touche);
  };
  const touche = (e) => e.key === "Escape" && fermer();
  document.addEventListener("keydown", touche);
  // on ferme en cliquant à côté, pas sur l'image elle-même
  fond.addEventListener("click", (e) => {
    if (e.target === fond || e.target.closest(".apercu-image__fermer")) fermer();
  });
  return fermer;
}

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

export function ilYA(ts) {
  if (!ts) return "";
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j === 1) return "hier";
  if (j < 7) return `il y a ${j} j`;
  const d = new Date(ts);
  return `${d.getDate()} ${MOIS[d.getMonth()]}${d.getFullYear() !== new Date().getFullYear() ? " " + d.getFullYear() : ""}`;
}

export function heure(ts) {
  const d = new Date(ts || Date.now());
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function jourLong(ts) {
  const d = new Date(ts || Date.now());
  const j = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"][d.getDay()];
  return `${j} ${d.getDate()} ${MOIS[d.getMonth()]}`;
}

/** Verset du jour : même verset pour tout le monde, change chaque jour. */
export function versetDuJour() {
  const liste = window.APP.versets;
  const jours = Math.floor(Date.now() / 864e5);
  return liste[jours % liste.length];
}

/* ------------------------------------------------------------------ notifications */

export function toast(message, type = "info") {
  const zone = $("#toasts");
  const el = document.createElement("div");
  el.className = `toast toast--${type}`;
  el.textContent = message;
  zone.appendChild(el);
  setTimeout(() => {
    el.classList.add("toast--out");
    setTimeout(() => el.remove(), 300);
  }, 3200);
}

/* ------------------------------------------------------------------------ modales */

let fermerCourant = null;

export function modale({ titre, corps, actions = [], large = false }) {
  fermerModale();
  const fond = document.createElement("div");
  fond.className = "modal-fond";
  fond.innerHTML = `
    <div class="modal${large ? " modal--large" : ""}" role="dialog" aria-modal="true" aria-label="${esc(titre || "")}">
      <div class="modal__tete">
        <h2>${esc(titre || "")}</h2>
        <button class="icon-btn" data-fermer aria-label="Fermer">${icone("fermer", { taille: 18 })}</button>
      </div>
      <div class="modal__corps">${corps}</div>
      ${actions.length ? `<div class="modal__pied">${actions.map((a, i) => `<button class="btn ${a.classe || "btn--ghost"}" data-action="${i}">${esc(a.label)}</button>`).join("")}</div>` : ""}
    </div>`;
  document.body.appendChild(fond);
  document.body.classList.add("no-scroll");

  const fermer = () => {
    fond.remove();
    document.body.classList.remove("no-scroll");
    document.removeEventListener("keydown", onKey);
    fermerCourant = null;
  };
  const onKey = (e) => e.key === "Escape" && fermer();
  document.addEventListener("keydown", onKey);
  fermerCourant = fermer;

  fond.addEventListener("click", (e) => {
    if (e.target === fond || e.target.closest("[data-fermer]")) return fermer();
    const btn = e.target.closest("[data-action]");
    if (btn) {
      const a = actions[+btn.dataset.action];
      if (a.onClick) a.onClick(fond, fermer);
      else fermer();
    }
  });
  return { el: fond, fermer };
}

export function fermerModale() {
  if (fermerCourant) fermerCourant();
}

export function confirmer(titre, texte, labelOk = "Confirmer") {
  return new Promise((resolve) => {
    modale({
      titre,
      corps: `<p class="texte-doux">${esc(texte)}</p>`,
      actions: [
        { label: "Annuler", onClick: (_, f) => { f(); resolve(false); } },
        { label: labelOk, classe: "btn--danger", onClick: (_, f) => { f(); resolve(true); } },
      ],
    });
  });
}

/* -------------------------------------------------------------------------- divers */

/* `autoTaille` (textarea qui grandit) vivait ici, sans plus aucun appelant
   depuis la migration React. Retirée le 20 octobre 2026 (audit) — archive :
   ~/Desktop/koinonia-archive-code-retire-2026-10-20.zip. */

/**
 * Ajoute un œil « afficher / masquer » à chaque champ de mot de passe.
 *
 * Sur un téléphone, taper un mot de passe à l'aveugle est la première cause
 * d'échec de connexion — et la personne finit par croire qu'elle l'a oublié.
 * Le champ reste masqué par défaut : on ne montre que sur demande explicite.
 *
 * @param {HTMLElement} racine  élément dans lequel chercher les champs
 */
export function brancherOeil(racine) {
  racine.querySelectorAll('input[type="password"]').forEach((champ) => {
    if (champ.dataset.oeil) return; // déjà équipé
    champ.dataset.oeil = "1";

    const enveloppe = document.createElement("span");
    enveloppe.className = "champ-mdp";
    champ.parentNode.insertBefore(enveloppe, champ);
    enveloppe.appendChild(champ);

    const bouton = document.createElement("button");
    bouton.type = "button";
    bouton.className = "champ-mdp__oeil";
    bouton.setAttribute("aria-label", "Afficher le mot de passe");
    bouton.innerHTML = icone("oeil", { taille: 19 });
    enveloppe.appendChild(bouton);

    bouton.addEventListener("click", () => {
      const montre = champ.type === "text";
      champ.type = montre ? "password" : "text";
      bouton.innerHTML = icone(montre ? "oeil" : "oeilBarre", { taille: 19 });
      bouton.setAttribute("aria-label", montre ? "Afficher le mot de passe" : "Masquer le mot de passe");
      // on rend la main au champ, curseur à la fin
      champ.focus();
      const n = champ.value.length;
      champ.setSelectionRange?.(n, n);
    });
  });
}

export const vide = (symbole, titre, texte = "") =>
  `<div class="vide"><div class="vide__icone">${symbole}</div><h3>${esc(titre)}</h3>${texte ? `<p>${esc(texte)}</p>` : ""}</div>`;

export const chargement = (texte = "Chargement…") =>
  `<div class="vide"><div class="spinner"></div><p>${esc(texte)}</p></div>`;
